import { FC } from 'react'
import { Box, Avatar, Link, styled } from '@mui/material'
import { getFullImageUrl } from 'utils'
import { Icon } from 'packages/eid-icons'
import { Tooltip } from 'packages/eid-ui'
import appConfig from 'config'
import { getURLTarget } from 'packages/core'

const StyledPersonCard = styled(Box)({
    width: '248px',
    height: '248px',
    margin: '26px 0 0',
    padding: '63px 1px 0',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.1)',
    backgroundColor: '#ffffff',
    position: 'relative',
    '&:hover': {
        cursor: 'context-menu',
    },
})

const StyledAvatarContainer = styled(Box)({
    backgroundColor: '#eef0f4',

    width: '82px',
    height: '82px',
    marginTop: '-104px',
    marginLeft: '83px',

    borderRadius: '50%',

    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
})

const StyledAvatar = styled(Avatar)({
    width: '62px',
    height: '62px',
    borderRadius: '50%',
})

const StyledContentContainer = styled(Box)({
    padding: '24px',
})

const StyledLink = styled(Link)({
    '&:hover': {
        textDecoration: 'none',
        '& span': {
            color: '#0057A0',
        },
    },
})

const StyledFirstName = styled(Box)({
    fontSize: '18px',
    fontWeight: 'bold',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 'normal',
    letterSpacing: 'normal',
    color: '#282828',
    marginRight: '4px',
})

const StyledLastName = styled(Box)({
    fontSize: '18px',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 'normal',
    letterSpacing: 'normal',
    color: '#282828',
})

interface StyledDepartmentProps {
    baseColor: string
    color: string
}

const StyledDepartment = styled(Box)<StyledDepartmentProps>(
    ({ color, baseColor }) => ({
        height: '20px',
        borderRadius: '2px',
        fontWeight: 600,
        fontSize: '9px',
        textTransform: 'uppercase',
        display: 'table',
        alignItems: 'center',
        padding: '5px 10px',
        margin: '8px 0px',
        backgroundColor: baseColor,
        color: color,
    }),
)

const StyledIconContainer = styled(Box)({
    width: '100%',
    height: '30px',
    borderTop: ' solid 0.8px #e8e8ec',
    borderBottom: ' solid 0.8px #e8e8ec',
    margin: '8px 0px',

    display: 'flex',
    alignItems: 'center',

    '& svg': {
        width: '16px',
        height: '16px',
        marginRight: '8px',
    },

    '& div': {
        display: 'flex',
        alignItems: 'center',
    },
    '& a': {
        display: 'flex',
        alignItems: 'center',
    },
})

const StyledPosition = styled(Box)({
    fontSize: '13px',
    fontWeight: 600,
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 'normal',
    letterSpacing: 'normal',
    color: '#9b9fa7',
})

interface StyledBottomBorderProps {
    color: string
}

const StyledBottomBorder = styled(Box)<StyledBottomBorderProps>(
    ({ color }) => ({
        width: '245px',
        height: '2px',
        border: 'solid 0.7px',
        borderColor: color,
        backgroundColor: color,
        position: 'absolute',
        bottom: 0,
    }),
)

export const Person: FC<{ person: any; colorValue: any }> = (props) => {
    const color = `rgb(${props.colorValue})`
    const baseColor = `rgba(${props.colorValue},0.38)`

    const { person } = props
    const personName = [person.firstName, person.lastName]
        .filter(Boolean)
        .join(' ')

    const showLinks =
        person.email ||
        person.phone ||
        person.facebookProfileUr ||
        person.linkedInProfileUrl ||
        person.instagramProfileUrl ||
        person.twitterProfileUrl

    return (
        <StyledPersonCard
            data-ai-explain={`Org chart person: ${
                [person.firstName, person.lastName].filter(Boolean).join(' ') ||
                'team member'
            }`}
            data-ai-explain-section="listing"
        >
            <StyledAvatarContainer>
                <StyledAvatar
                    src={getFullImageUrl(person.imageThumbUrl)}
                    alt={personName}
                />
            </StyledAvatarContainer>

            <StyledContentContainer>
                <StyledLink
                    href={`${appConfig.APP_SUBPATH}/whitepages/people/${person.id}`}
                    target={getURLTarget()}
                >
                    <StyledFirstName
                        component={'span'}
                        style={{ wordBreak: 'break-all' }}
                    >
                        {person.firstName}
                    </StyledFirstName>
                    <StyledLastName component={'span'}>
                        {person.lastName}
                    </StyledLastName>
                </StyledLink>

                {(person.jobTitle || person.country) && (
                    <StyledPosition>
                        {person.jobTitle && `${person.jobTitle} `}
                        {person.country}
                    </StyledPosition>
                )}

                {showLinks && (
                    <StyledIconContainer>
                        {person.email && (
                            <a
                                href={`mailto:${person.email}`}
                                aria-label={`Email: ${person.email}`}
                            >
                                <Tooltip
                                    title={person.email}
                                    disableInteractive={false}
                                >
                                    <div>
                                        <Icon name="EmailRound" />
                                    </div>
                                </Tooltip>
                            </a>
                        )}
                        {person.phone && (
                            <a
                                href={`tel:${person.phone}`}
                                aria-label={`Phone: ${person.phone}`}
                            >
                                <Tooltip
                                    title={person.phone}
                                    disableInteractive={false}
                                >
                                    <div>
                                        <Icon name="PhoneRound" />
                                    </div>
                                </Tooltip>
                            </a>
                        )}
                        {person.facebookProfileUrl && (
                            <Link
                                target={getURLTarget()}
                                href={`${person.facebookProfileUrl}`}
                                aria-label="Facebook"
                            >
                                <Icon name="Facebook" />
                            </Link>
                        )}
                        {person.instagramProfileUrl && (
                            <Link
                                target={getURLTarget()}
                                href={`${person.instagramProfileUrl}`}
                                aria-label="Instagram"
                            >
                                <Icon name="Instagram" />
                            </Link>
                        )}

                        {person.linkedInProfileUrl && (
                            <Link
                                target={getURLTarget()}
                                href={`${person.linkedInProfileUrl}`}
                                aria-label="LinkedIn"
                            >
                                <Icon name="LinkedinRound" />
                            </Link>
                        )}
                        {person.twitterProfileUrl && (
                            <Link
                                target={getURLTarget()}
                                href={`${person.twitterProfileUrl}`}
                                aria-label="Twitter"
                            >
                                <Icon name="TwitterRound" />
                            </Link>
                        )}
                    </StyledIconContainer>
                )}
                {person.department && (
                    <StyledDepartment color={color} baseColor={baseColor}>
                        {person.department}
                    </StyledDepartment>
                )}
            </StyledContentContainer>

            <StyledBottomBorder color={color} />
        </StyledPersonCard>
    )
}
